// ═══════════════════════════════════════════════════════════════
// EMAIL TEMPLATES SERVICE
// Path: src/services/emailTemplatesService.js
//
// Thin wrapper over settingsService for the "email_templates"
// app_settings key. Same storage pattern as chapter_settings —
// one JSON blob keyed by template_key with { subject, body }
// overrides. Anything not overridden falls back to the defaults
// in constants/emailTemplates.js.
// ═══════════════════════════════════════════════════════════════

import { settingsService } from "./settingsService";
import { debugLog } from "../utils/debugLog";
import { mergeTemplates } from "../constants/emailTemplates";

const SETTINGS_KEY = "email_templates";

export const emailTemplatesService = {
  // Load all templates: defaults merged with chapter overrides
  async getAll() {
    try {
      const overrides = (await settingsService.get(SETTINGS_KEY)) || {};
      return mergeTemplates(overrides);
    } catch (err) {
      debugLog.error("emailTemplatesService.getAll", err);
      return mergeTemplates({});
    }
  },

  // Update one template's subject and/or body
  async update(templateKey, patch) {
    try {
      const overrides = (await settingsService.get(SETTINGS_KEY)) || {};
      const existing = overrides[templateKey] || {};
      overrides[templateKey] = {
        ...existing,
        ...patch,
        updatedAt: new Date().toISOString(),
      };
      await settingsService.set(SETTINGS_KEY, overrides);
      debugLog.action("emailTemplatesService.update", { templateKey });
      return mergeTemplates(overrides);
    } catch (err) {
      debugLog.error("emailTemplatesService.update", err);
      throw err;
    }
  },

  // Reset one template to its default (remove the override)
  async reset(templateKey) {
    try {
      const overrides = (await settingsService.get(SETTINGS_KEY)) || {};
      delete overrides[templateKey];
      await settingsService.set(SETTINGS_KEY, overrides);
      debugLog.action("emailTemplatesService.reset", { templateKey });
      return mergeTemplates(overrides);
    } catch (err) {
      debugLog.error("emailTemplatesService.reset", err);
      throw err;
    }
  },
};
